import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Printer, ArrowLeft, Download } from "lucide-react";
import DOMPurify from "dompurify";

const RESOURCES = [
  { type: "billard", name: "Billard Tisch", count: 10, color: "#10b981" },
  { type: "edart", name: "eDart Automat", count: 12, color: "#06b6d4" },
  { type: "steeldart", name: "Steeldart Board", count: 2, color: "#dc2626" },
];

export default function QRCodesPage() {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [activeTab, setActiveTab] = useState("billard");
  const baseUrl = window.location.origin;

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open("", "_blank");
    if (!win) return;
    const doc = win.document;
    doc.open();

    const style = doc.createElement("style");
    style.textContent = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Arial', sans-serif; padding: 20px; background: white; color: black; }
      .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
      .qr-card { border: 2px solid #e5e7eb; border-radius: 8px; padding: 16px; text-align: center; page-break-inside: avoid; }
      .qr-card h3 { font-size: 14px; font-weight: bold; margin-bottom: 8px; text-transform: uppercase; }
      .qr-card p { font-size: 10px; color: #6b7280; margin-top: 6px; }
      .qr-card svg { margin: 0 auto; }
      h1 { font-size: 24px; margin-bottom: 4px; text-transform: uppercase; }
      h2 { font-size: 14px; color: #6b7280; margin-bottom: 20px; }
      @media print { .grid { grid-template-columns: repeat(3, 1fr); } }
    `;
    doc.head.appendChild(style);
    doc.title = "QR Codes - Ninersbillard";

    const h1 = doc.createElement("h1");
    h1.textContent = "Ninersbillard QR Codes";
    doc.body.appendChild(h1);

    const h2 = doc.createElement("h2");
    h2.textContent = RESOURCES.find(r => r.type === activeTab)?.name || "";
    doc.body.appendChild(h2);

    const container = doc.createElement("div");
    container.innerHTML = DOMPurify.sanitize(content.innerHTML);
    doc.body.appendChild(container);

    doc.close();
    win.onload = () => win.print();
  };

  const token = localStorage.getItem("nb_token");
  if (!token) {
    navigate("/admin-login");
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="qr-codes-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 glass-header border-b border-white/10">
        <div className="flex items-center justify-between px-4 md:px-8 h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              data-testid="qr-back-btn">
              <ArrowLeft className="w-4 h-4" /> Admin
            </button>
            <span className="font-bold text-lg uppercase tracking-tight" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>
              QR Codes
            </span>
          </div>
          <button onClick={handlePrint} data-testid="qr-print-btn"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase">
            <Printer className="w-4 h-4" /> Drucken
          </button>
        </div>
      </header>

      <div className="px-4 md:px-8 py-8 max-w-6xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald' }}>
          QR-Code <span style={{ color: '#fbbf24' }}>System</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Drucke die QR-Codes aus und platziere sie auf den Tischen und Automaten. Gäste scannen den Code und gelangen direkt zur Speisekarte, Reservierung oder Eventübersicht.
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="qr-tabs">
          <TabsList className="flex h-auto gap-1 bg-transparent p-0 mb-8">
            {RESOURCES.map(res => (
              <TabsTrigger key={res.type} value={res.type}
                className="px-4 py-2 border border-white/10 text-xs font-bold uppercase tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary"
                data-testid={`qr-tab-${res.type}`}>
                {res.name} ({res.count})
              </TabsTrigger>
            ))}
          </TabsList>

          {RESOURCES.map(res => (
            <TabsContent key={res.type} value={res.type}>
              <div ref={res.type === activeTab ? printRef : null}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {Array.from({ length: res.count }, (_, i) => i + 1).map(num => {
                    const url = `${baseUrl}/qr/${res.type}/${num}`;
                    return (
                      <div key={num} className="qr-card bg-card border border-white/5 p-4 text-center hover:border-white/20 transition-colors duration-200"
                        data-testid={`qr-card-${res.type}-${num}`}>
                        <h3 className="text-xs font-bold uppercase tracking-widest mb-3 truncate" style={{ fontFamily: 'Oswald', color: res.color }}>
                          {res.name} #{num}
                        </h3>
                        <div className="flex justify-center mb-3">
                          <QRCodeSVG
                            value={url}
                            size={120}
                            bgColor="#18181b"
                            fgColor="#fafafa"
                            level="M"
                            includeMargin={true}
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground break-all leading-tight">{url}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Instructions */}
        <div className="mt-12 bg-card border border-white/5 p-6 max-w-2xl" data-testid="qr-instructions">
          <h2 className="text-xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>
            Anleitung
          </h2>
          <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
            <li>Wähle die Kategorie (Billard, eDart oder Steeldart)</li>
            <li>Klicke auf <strong className="text-foreground">&quot;Drucken&quot;</strong> um alle QR-Codes der Kategorie auszudrucken</li>
            <li>Schneide die einzelnen QR-Codes aus</li>
            <li>Platziere jeden QR-Code auf dem entsprechenden Tisch oder Automaten</li>
            <li>Gäste scannen den Code und sehen sofort Speisekarte, Verfügbarkeit und können reservieren</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
